import React from 'react';
import { ModalKey } from '@dreamai/types';
import { useOnboardingModals } from '../../../providers';
import TransactionalSheet, { SheetPresentation } from './TransactionalSheet';
import {
  CreditCardLockIcon,
  ShoppingBagIcon,
  WalletIcon,
} from './transactional-icons';
import './spending-onboarding-modal.scss';

interface SpendingOnboardingModalProps {
  show: boolean;
  onNext: () => void;
  /**
   * In-person purchases can only be paid with a Dream-controlled card (the
   * Assistant Card, or a card funded by an organisation).
   */
  dreamCardOnly: boolean;
  explainDeposit?: boolean;
  presentation?: SheetPresentation;
}

const SpendingOnboardingModal: React.FC<SpendingOnboardingModalProps> = ({
  show,
  onNext,
  dreamCardOnly,
  explainDeposit,
  presentation = 'modal',
}) => {
  const { markAsSeen } = useOnboardingModals();

  const modalKey: ModalKey = explainDeposit
    ? 'deposit_onboarding'
    : dreamCardOnly
      ? 'spending_onboarding_agent_only'
      : 'spending_onboarding_both';

  const handleNext = async () => {
    await markAsSeen(modalKey);
    onNext();
  };

  const title = explainDeposit
    ? 'I can reserve things for you'
    : dreamCardOnly
      ? 'I can buy things in person'
      : 'I can buy things';

  return (
    <TransactionalSheet
      show={show}
      presentation={presentation}
      leading={<ShoppingBagIcon />}
      ariaLabelledBy='spend-onboarding-title'
      className='spending-onboarding-modal'
      backdropClassName='spending-approval-backdrop'
      footer={
        <button
          className='spend-onboarding-next-btn tx-btn-primary'
          onClick={handleNext}
          type='button'
        >
          Next
        </button>
      }
    >
      <div className='tx-head'>
        <h1 className='tx-title' id='spend-onboarding-title'>
          {title}
        </h1>
        {explainDeposit && (
          <p className='tx-sub'>
            I'm able to make reservations for you. Some reservations need a card
            &mdash; this could be a deposit, a hold, or a no-show guarantee
            depending on the venue.
          </p>
        )}
        {!explainDeposit && !dreamCardOnly && (
          <p className='tx-sub'>
            I'm able to buy things for you, or have one of my Dreamsters do the
            same in the real world. It's safe, secure, and you choose how I do
            it.
          </p>
        )}
        {!explainDeposit && dreamCardOnly && (
          <p className='tx-sub'>
            One of my team (Dreamsters) will pay for your item in person.
          </p>
        )}
      </div>

      <div className='spend-onboarding-options'>
        {!explainDeposit && (
          <div className='spend-onboarding-option'>
            <span className='spend-onboarding-option-icon' aria-hidden='true'>
              <WalletIcon />
            </span>
            <div className='spend-onboarding-option-text'>
              <h2>My card</h2>
              {!dreamCardOnly && (
                <p>
                  I have a prepaid credit card. You top it up and I pay. The
                  means you <b>avoid</b> needing to go through further
                  authorization when I buy your item.
                </p>
              )}
              {dreamCardOnly && (
                <p>
                  I have a prepaid credit card. You top it up and I pay. For
                  online purchases you'll be able to add your own card.
                </p>
              )}
            </div>
          </div>
        )}

        {(!dreamCardOnly || explainDeposit) && (
          <div className='spend-onboarding-option'>
            <span className='spend-onboarding-option-icon' aria-hidden='true'>
              <CreditCardLockIcon />
            </span>
            <div className='spend-onboarding-option-text'>
              <h2>Your card</h2>
              <p>
                {explainDeposit
                  ? "You'll set a card hold limit for each reservation. Your card is stored securely and only used if the venue requires it \u2014 never above the limit you set."
                  : "For online purchases you can add your own card. It's stored securely, and you may need to share a verification code when the item is bought."}
              </p>
            </div>
          </div>
        )}
      </div>
    </TransactionalSheet>
  );
};

export default SpendingOnboardingModal;
